import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight } from 'lucide-react';
import { usePasswordStore } from '../store/passwordStore';

interface PasswordProtectionProps {
  onSuccess: () => void;
}

export function PasswordProtection({ onSuccess }: PasswordProtectionProps) {
  const { validatePassword } = usePasswordStore();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (validatePassword(password)) {
        onSuccess();
      } else {
        setError('Incorrect password');
        setPassword('');
      }
    } catch (error) {
      console.error('Error validating password:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4">
      {/* Ambient Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-purple-500/10 to-background"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/10 via-cyan-500/5 to-transparent"></div>
        <div className="absolute inset-0 backdrop-blur-[100px]"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-sm"
      >
        <form onSubmit={handleSubmit} className="theme-card p-6 space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <div className="relative">
                <Lock className="w-8 h-8 text-primary" />
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
              </div>
            </div>
            <h2 className="text-xl font-semibold">Enter Password</h2>
            <p className="text-sm text-muted-foreground">
              Please enter the password to access TODAY Move
            </p>
          </div>

          <div className="space-y-2">
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="Enter password"
              className="w-full px-4 py-2 bg-muted/50 rounded-lg border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
              disabled={isSubmitting}
            />
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-destructive"
              >
                {error}
              </motion.p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full theme-button py-2 rounded-lg flex items-center justify-center gap-2 relative group overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary opacity-0 group-hover:opacity-20 transition-opacity" />
            <span className="relative">Continue</span>
            <ArrowRight className="w-4 h-4 relative group-hover:translate-x-0.5 transition-transform" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}